// owner-map: OP-05b.spa
import { fetchResolveTrace, postResolve } from "../services/forecastRowApi.js";
import { useRef, useCallback, useEffect } from "react";
import { resolutionSet } from "../lib/forecast/reducer.js";
import { selectRun } from "../lib/forecast/selectors.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import { parseResolveDateLocal } from "../lib/formatDates.js";
import { isConcluded } from "../lib/tracking.js";
import { getRunState, runStore } from "../store/runStore.js";

/**
 * Resolver-poll domain: in-flight poll tracking ref, startResolverPoll
 * callback, and cleanup effect. Submits a resolve request to the server and
 * polls until a terminal verdict arrives, then stamps it into the run store
 * (the singleton, through `getRunState` / `runStore.dispatch`: no store handles are threaded in).
 *
 * `startResolverPoll` RESOLVES TO the verdict object it stamped, or to `null`
 * when nothing landed (tracking off, resolver unavailable, submit/poll failure,
 * timeout, abort). The reforecast command awaits it to decide whether a
 * forecast is still warranted; every failure is also recorded internally as a
 * `ui.resolver.failed` event (log ring buffer → bug-report bundle) so the user
 * never sees a status code while the raw reason stays recoverable. A caller that wants the
 * refusal itself (Check now's hourly limit) passes `onSubmitFailed`, which receives the submit's
 * `{status, body}` when the server turns the request away.
 *
 * @param {{ forecastTracking: boolean, mockToday: string|null }} args
 * @returns {{ startResolverPoll: Function }}
 */
export function useResolverPoll({ forecastTracking, mockToday }) {
  // resolverPollAbortsRef tracks in-flight polls keyed by forecast id so
  // a second reforecast on the same forecast cancels the first poll
  // (otherwise two competing polls would race to write resolution state).
  const resolverPollAbortsRef = useRef(new Map());
  const startResolverPoll = useCallback(
    async (forecastObj, { onSubmitFailed } = {}) => {
      if (!forecastTracking) return null;
      if (!forecastObj || !forecastObj.id) return null;
      const fcId = forecastObj.id;
      const contract = forecastObj.contract || {};
      if (!contract.resolution_date) return null;
      const failed = (reason, extra = {}) =>
        recordUiEvent({
          event: "ui.resolver.failed",
          forecastId: forecastObj.forecastId || null,
          reason,
          ...extra,
        });
      // Never DOWNGRADE the live run: the realtime contract.resolved pickup can stamp a
      // terminal verdict while this poll is out, and the reforecast command decides on the
      // live run after awaiting us — an `in_flight` marker or an `unresolved` result written
      // over that verdict would make it forecast a settled question. Read the external store
      // at write time: it reflects every dispatch synchronously, whereas a render-time
      // snapshot only catches up on the next render.
      // Either slice: an in-org collaborator's Update now polls a forecast shared with them.
      const liveRun = () => selectRun(getRunState(), fcId); // the one live-state read
      const liveIsTerminal = () => isConcluded(liveRun());
      // Cancel any prior in-flight poll for this forecast.
      const prevAc = resolverPollAbortsRef.current.get(fcId);
      if (prevAc) {
        try {
          prevAc.abort();
        } catch (_e) {
          /* already aborted */
        }
      }
      // Clear the aborted poll's stale {status:"in_flight"} marker now only when
      // the stored value IS an in-flight marker (not a terminal verdict). The new
      // submit below sets a fresh in-flight marker on success — but if it FAILS
      // (e.g. resolver 503), returning early would otherwise strand the old marker
      // and leave the UI stuck on "Resolving…" (RESOLUTION report section
      // suppressed) indefinitely. Guard against clobbering a terminal verdict that
      // arrived before the previous poll was cancelled.
      let forecastUuid = forecastObj.forecastId;
      {
        const currentRun = liveRun();
        forecastUuid ||= currentRun?.forecastId;
        const currentResolution = currentRun?.resolution;
        if (currentResolution?.status === "in_flight") {
          runStore.dispatch(resolutionSet(fcId, null)); // dual-write resolution clear to the store
        }
      }
      const ac = new AbortController();
      resolverPollAbortsRef.current.set(fcId, ac);
      // HARD deadline. The elapsed check inside the loop only runs between iterations,
      // so a submit or poll fetch that never settles would otherwise hold this promise —
      // and, since the reforecast command awaits it, the foreground spinner and the exec
      // claim — open forever. Aborting the controller at the ceiling rejects whichever
      // fetch is pending. `deadlineHit` tells that abort apart from a superseding poll's.
      const MAX_POLL_MS = 5 * 60 * 1000;
      const startedAt = Date.now();
      let deadlineHit = false;
      const deadline = setTimeout(() => {
        deadlineHit = true;
        ac.abort();
      }, MAX_POLL_MS);
      let lastPollError = null;
      const timeoutDetails = (traceId) => ({
        trace_id: traceId ?? null,
        elapsed_ms: Date.now() - startedAt,
        ...(lastPollError ? { last_error: String(lastPollError) } : {}),
      });
      try {
        const startDate = forecastObj.createdAt
          ? new Date(forecastObj.createdAt).toISOString().slice(0, 10)
          : null;
        const payload = {
          forecastId: forecastUuid || undefined,
          question: contract.question || contract.title || forecastObj.question,
          background: contract.background || undefined,
          resolution_criteria: contract.resolution_criteria || undefined,
          start_date: startDate || undefined,
          end_date: contract.resolution_date,
        };
        let traceId;
        try {
          const r = await postResolve(fcId, payload, { signal: ac.signal });
          const data = await r.json().catch(() => ({}));
          if (!r.ok) {
            onSubmitFailed?.({ status: r.status, body: data });
            // 503 → resolver_unavailable (no token / metadata server). Quiet
            // log; user keeps using the app, just no verdict this run.
            if (r.status === 503) {
              console.info("[resolver] unavailable:", data.detail || "no credential");
              failed("unavailable", { status: 503, detail: data.detail || null });
              return null;
            }
            // 429 → this forecast was checked within the hour (server/handlers/resolver.js);
            // a reforecast goes ahead without asking again.
            if (r.status === 429) {
              console.info("[resolver] checked within the hour; not asking again");
              failed("cooldown", { status: 429, available_at: data.available_at || null });
              return null;
            }
            console.warn("[resolver] submit failed", r.status, data);
            failed("submit_failed", { status: r.status });
            return null;
          }
          traceId = data.trace_id;
          if (!traceId) {
            console.warn("[resolver] no trace_id in response", data);
            failed("no_trace_id");
            return null;
          }
          // Superseded (or cleaned up / past the deadline) while the submit was in flight, yet
          // the response settled anyway: stamping now would overwrite the newer poll's marker
          // with this trace_id, and that poll's own clear would then no longer match.
          if (ac.signal.aborted) {
            if (deadlineHit) failed("timeout", timeoutDetails(traceId));
            return null;
          }
          // Stamp in-flight state so the UI can show "Resolving…" while we poll.
          const inflight = {
            status: "in_flight",
            trace_id: traceId,
            requestedAt: new Date().toISOString(),
          };
          if (!liveIsTerminal()) runStore.dispatch(resolutionSet(fcId, inflight)); // dual-write in-flight resolution to the store
        } catch (e) {
          if (e.name !== "AbortError") {
            console.warn("[resolver] submit threw", e);
            failed("submit_threw", { error: String(e) });
            onSubmitFailed?.({ status: 0, body: null });
          } else if (deadlineHit) {
            failed("timeout", timeoutDetails(null));
            onSubmitFailed?.({ status: 0, body: null });
          }
          return null;
        }
        // Clear OUR in-flight marker only when the stored value still matches THIS
        // poll's trace_id — a superseding poll will have already set its own
        // in-flight marker with a different trace_id, and a terminal verdict must
        // never be clobbered.
        const clearInflight = () => {
          const cur = liveRun()?.resolution;
          if (cur?.status === "in_flight" && cur?.trace_id === traceId) {
            runStore.dispatch(resolutionSet(fcId, null)); // dual-write in-flight resolution clear to the store
          }
        };
        // Every abort clears OUR marker — clearInflight compares trace_id, so a superseding
        // poll's fresh marker and any terminal verdict survive — and only the deadline is a
        // failure worth recording; an unmount / tracking-off / supersession abort is not.
        const onAbort = () => {
          if (deadlineHit) {
            console.warn("[resolver] timeout for", fcId, traceId);
            failed("timeout", timeoutDetails(traceId));
          }
          clearInflight();
          return null;
        };
        // Poll every 10s, max 5min (the deadline timer above is the hard stop).
        const POLL_INTERVAL_MS = 10 * 1000;
        while (!ac.signal.aborted) {
          await new Promise((r) => {
            const t = setTimeout(r, POLL_INTERVAL_MS);
            ac.signal.addEventListener(
              "abort",
              () => {
                clearTimeout(t);
                r();
              },
              { once: true },
            );
          });
          if (ac.signal.aborted) return onAbort();
          if (Date.now() - startedAt > MAX_POLL_MS) {
            console.warn("[resolver] timeout for", fcId, traceId);
            failed("timeout", timeoutDetails(traceId));
            clearInflight();
            return null;
          }
          try {
            const r = await fetchResolveTrace(fcId, traceId, forecastUuid, {
              signal: ac.signal,
            });
            if (r.status === 404) {
              const status = await r.json().catch(() => ({}));
              if (status.status === "in_flight") continue;
              failed("poll_not_found", { trace_id: traceId, status: 404 });
              clearInflight();
              return null;
            }
            if (!r.ok) {
              console.warn("[resolver] poll non-ok", r.status);
              failed("poll_failed", { trace_id: traceId, status: r.status });
              clearInflight();
              return null;
            }
            const verdict = await r.json();
            if (!verdict || !verdict.status || verdict.status === "in_flight") continue;
            // Stamp the verdict. Updates BOTH active (visible state) and the
            // forecasts[] entry (saved-list row) so a navigation away + back
            // also shows the resolved state. Server-side persistence in the
            // GET poll route writes it to server_ctx.resolution too. The
            // timeline render gates the visual on whether the verdict is
            // terminal OR (unresolved AND past) — stamping always so the
            // data persists for when the date eventually passes.
            // A terminal live state wins over a non-terminal result (see liveIsTerminal).
            if (!liveIsTerminal() || isConcluded({ ...forecastObj, resolution: verdict })) {
              runStore.dispatch(resolutionSet(fcId, verdict)); // dual-write verdict resolution to the store
            }
            // Record only a compact summary (status + trace_id) — NOT the full
            // verdict.resolution, which can carry large reasoning/evidence blobs that
            // would bloat the sessionStorage-backed log ring buffer and risk blowing
            // the quota (silently disabling log persistence for bug-report triage).
            recordUiEvent({
              event: "ui.resolver.verdict",
              forecastId: forecastObj.forecastId || null,
              status: verdict.status,
              trace_id: traceId,
            });
            // Per UX spec: when the resolver returns "unresolved" but the
            // resolution date is still in the future, the timeline visual
            // doesn't change — we just announce it in the chat console so
            // the user knows the resolver actually ran and reported back.
            // parseResolveDateLocal: date-only strings → LOCAL midnight, matching the
            // rest of the FT gating (so this "deadline in the future?" check doesn't
            // fire hours early/late vs the timeline in non-UTC zones).
            const resDateMs = contract.resolution_date
              ? parseResolveDateLocal(contract.resolution_date).getTime()
              : null;
            // Use the debug mock-today instant when set so this "deadline still in
            // the future?" check agrees with the timeline/report gating (which both
            // key off effectiveToday). Falls back to real wall-clock when no mock.
            const nowMs = mockToday ? new Date(mockToday).getTime() : Date.now();
            const isUnresolvedFuture =
              verdict.status === "unresolved" && resDateMs != null && resDateMs >= nowMs;
            void isUnresolvedFuture;
            return verdict;
          } catch (e) {
            if (e.name === "AbortError") return onAbort();
            lastPollError = e;
            console.warn("[resolver] poll threw", e);
            // Don't abort — keep trying until timeout; the timeout event carries this error.
          }
        }
        return onAbort();
      } finally {
        clearTimeout(deadline);
        // Drop our entry so the Map doesn't grow unbounded across
        // reforecasts/forecasts — but only if a newer poll for this fcId
        // hasn't already replaced us (a second reforecast on the same
        // forecast sets a fresh controller we must not clobber).
        if (resolverPollAbortsRef.current.get(fcId) === ac)
          resolverPollAbortsRef.current.delete(fcId);
      }
    },
    [forecastTracking, mockToday],
  );

  // Abort + clear all in-flight resolver polls on unmount and whenever
  // Forecast Tracking is switched off, so aborted/completed controllers
  // never linger in the ref (belt-and-suspenders to the per-poll finally
  // cleanup above, which handles the normal exit paths).
  useEffect(() => {
    const polls = resolverPollAbortsRef.current;
    return () => {
      for (const ac of polls.values()) {
        try {
          ac.abort();
        } catch (_e) {
          /* already aborted */
        }
      }
      polls.clear();
    };
  }, [forecastTracking]);

  return { startResolverPoll };
}
